import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './graph-container.module.scss';
import BatteryChart from '../graphs/battery-chart/BatteryChart';
import NewAreaChart from '../graphs/area-chart/areachart';
// Original import kept for reference/rollback:
// import { Stackbar } from '../graphs/stackbar-graph/stackbar';
// TEST: Using StackbarFixedRadius for proper corner radius handling
import { StackbarFixedRadius as Stackbar } from '../graphs/stackbar-graph/stackbar-fixed-radius';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ChartSkeleton, Shimmer } from '@flexo/atoms';
import { useKpiData } from '@flexo/hooks';
import { formatDate } from '@flexo/general';
import { maskKpiDataAfterUpperBound } from '../graphs/helpers';
const ENV = (import.meta as any).env;

const GraphContainer = React.memo((props: any) => {
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendarUpperBound = useSelector(
    (state: any) => state?.appConfig?.calendar?.upperBound || null
  );
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  const ThemeStore = useSelector((state: any) => state.theme);
  const [graphValues, setGraphValues] = useState([]);

  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  if (props?.kpiContext === 'economics') {
    kpiContext = 'Economics';
  }
  if (props?.kpiContext === 'ancillary') {
    kpiContext = 'Ancillary';
  }

  const rawDetailKpiData: any = kpi[`${kpiContext}${entity}`]?.['1hour'] || null;
  const rawOverviewKpiData = kpi[`${kpiContext}${entity}`]?.['1day'] || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // Use raw KPI data directly - fillMissingTimestamps was causing issues with
  // sub-day granularities (15min, 1hour) by normalizing all timestamps to startOf('day')
  const detailKpiData = useMemo(
    () => maskKpiDataAfterUpperBound(rawDetailKpiData, calendarUpperBound),
    [rawDetailKpiData, calendarUpperBound]
  );
  const overviewKpiData = useMemo(
    () => maskKpiDataAfterUpperBound(rawOverviewKpiData, calendarUpperBound),
    [rawOverviewKpiData, calendarUpperBound]
  );

  // const LOADING_STATE = 'PENDING';

  // Use the new hook for KPI data access
  const { findApiUnit, findApiValue } = useKpiData(
    detailKpiData?.[0]?.kpi,
    kpiContext.toLowerCase()
  );

  useEffect(() => {
    let filteredKeys = props?.graphKeys?.filter((el) =>
      detailKpiData?.[0]?.kpi.some(
        (kpiItem) => kpiItem.name === (typeof el === 'string' ? el : el?.key)
      )
    );

    if (kpiContext === 'Economics') {
      const availableData = (overviewKpiData?.[0]?.kpi || [])?.filter(
        (datum) =>
          datum.value !== null &&
          datum.value !== '' &&
          datum.value !== undefined
      );

      filteredKeys = filteredKeys?.filter((el) =>
        availableData.some(
          (kpi) => kpi.name === (typeof el === 'string' ? el : el?.key)
        )
      );
    }

    setGraphValues(filteredKeys);
  }, [props.graphKeys, detailKpiData, overviewKpiData, kpiContext]);

  const getTranformDataAreachart = (data, graphKeys) => {
    return data?.map((entry) => {
      const kpiMap = {};
      entry?.kpi?.forEach((kpiItem) => {
        graphKeys?.forEach((key) => {
          if (kpiItem.name === key) {
            const formattedKey = `widgets.${props.subtype}.${key}`;
            kpiMap[formattedKey] = kpiItem.value?.value || kpiItem.value;
          }
        });
      });

      return {
        date: entry.timestamp,
        ...kpiMap,
      };
    });
  };

  const transformData = (data, graphKeys) => {
    return data?.map((entry) => {
      const kpiMap = {};
      entry?.kpi?.forEach((kpiItem) => {
        graphKeys?.forEach((key) => {
          if (kpiItem.name === key.key) {
            const formattedKey = `widgets.${props.subtype}.${key.key}`;
            kpiMap[formattedKey] = kpiItem.value?.value || kpiItem.value;
          }
        });
      });

      return {
        date: entry.timestamp,
        ...kpiMap,
      };
    });
  };

  const transformedData = transformData(
    detailKpiData ?? detailKpiData,
    graphValues
  );

  const getColors = () => {
    try {
      if (typeof props.colors[0] === 'string') {
        return props.colors?.[0].startsWith('#')
          ? props.colors
          : props.colors.map((color) => ThemeStore?.colors[color]);
      }
      return props.colors.map(
        (color) => ThemeStore?.colors[color?.color] + (color?.colorShade || '')
      );
    } catch (err) {
      return [...Array(props.buttons?.length || 0)].map(
        (_el, i) => `#000000${(i + 1) * 10}`
      );
    }
  };

  const getGraphButtons = (keys) => {
    return keys?.map((key) => `widgets.${props.subtype}.${key.key}`);
  };

  const getUnit = () => {
    const key =
      (typeof (graphValues as any)?.[0] === 'string'
        ? (graphValues as any)?.[0]
        : (graphValues as any)?.[0]?.key) || null;

    if (!key) {
      // console.warn('no key found');
      return '';
    }
    return findApiUnit(key);
  };

  const getGraphButtonsAreaChart = (keys) => {
    const graphButtons = keys?.map((key) => {
      return `widgets.${props.subtype}.${key}`;
    });

    return graphButtons;
  };

  const getTotalValue = (data) => {
    if (!props.totalValue) {
      return null;
    }
    return data?.map((entry) => {
      const kpiItem = entry.kpi.find(
        (kpiItem) => kpiItem.name === props.totalValue
      );
      return {
        date: entry.timestamp,
        totalValue: kpiItem?.value,
        unit: kpiItem?.unit,
      };
    });
  };
  const totalValue = getTotalValue(detailKpiData ?? detailKpiData);

  const ref = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const updateWidth = () => {
      if (ref.current) {
        setContainerWidth(ref.current.offsetWidth - 50);
      }
    };

    // Run updateWidth initially to get the first size
    updateWidth();

    // Use ResizeObserver to listen for changes
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(ref.current);

    // Cleanup function to disconnect observer
    return () => {
      observer.disconnect();
    };
  }, [ref.current]);

  const getGraph = () => {
    switch (props.subtype) {
      case 'consumptionStackBar':
      case 'productionStackBar':
      case 'savingsStackbar':
      case 'savingsStackBar':
        return (
          <Stackbar
            _data={transformedData}
            originalData={transformedData}
            graphKeys={
              graphValues && graphValues.length > 0
                ? props.graphKeys.filter((el) =>
                    graphValues?.some(
                      (datum: any) =>
                        datum?.key ===
                        (typeof el === 'string' ? el : (el as any)?.key)
                    )
                  )
                : props.graphKeys
            }
            title={props.title}
            unit={getUnit()}
            subtype={props.subtype}
            totalValue={totalValue ?? null}
            isLoading={
              LOADING_STATE === 'PENDING' &&
              (!transformedData || transformedData?.length === 0)
            }
            isLoadingError={LOADING_STATE === 'ERROR'}
          />
        );
      case 'areachart':
      case 'areaChart':
        return (
          <NewAreaChart
            colors={getColors()}
            title={props.title}
            data={getTranformDataAreachart(detailKpiData, props.graphKeys)}
            keys={getGraphButtonsAreaChart(props.graphKeys)}
            unit={getUnit()}
            containerWidth={containerWidth}
            isLoading={LOADING_STATE === 'PENDING' && (!transformedData || transformedData?.length === 0)}
          />
        );
      case 'batteryChartMobile':
        return <BatteryChart {...props} />;
      default:
        return (
          <Stackbar
            _data={transformedData}
            title={props.title}
            unit={getUnit()}
            graphKeys={props.graphKeys}
            subtype={props.subtype}
            totalValue={totalValue ?? null}
          />
        );
    }
  };

  return (
    <div
      className={`${styles.GraphContainer} ${
        props.gradientBorder && styles.GraphContainer__GradientBorder
      } graph-container`}
      style={{ position: 'relative', height: '100%' }}
      ref={ref}
    >
      <div
        className={
          ENV?.VITE_APP_IS_WEB === 'true'
            ? styles.GraphContainer__Web
            : styles.GraphContainer
        }
      >
        {LOADING_STATE === 'PENDING' && <Shimmer borderRadius={'16px'} />}

        <div className={styles.Graph}>{getGraph()}</div>
      </div>
    </div>
  );
});

export default GraphContainer;
